import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/integrations/supabase/client";

export interface CalendarEvent {
  id: string;
  summary: string;
  start: string | null;
  end: string | null;
  htmlLink: string | null;
}

export interface UseUserGoogleCalendarOptions {
  /** If set, the edge function will use this brand's api_tokens for Calendar OAuth (same brand as API Tokens → Google Calendar page). Pass currentBrandId from useBrandSelection() when available. */
  brandId?: string | null;
}

export function useUserGoogleCalendar(options?: UseUserGoogleCalendarOptions) {
  const brandId = options?.brandId ?? null;
  const [isLoading, setIsLoading] = useState(false);
  const [connectionCheckLoading, setConnectionCheckLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const checkConnection = useCallback(async () => {
    setConnectionCheckLoading(true);
    try {
      const { data, error } = await supabase
        .from("os_user_google_calendar_tokens")
        .select("email")
        .maybeSingle();

      if (error) throw error;
      if (data?.email) {
        setIsConnected(true);
        setConnectedEmail(data.email);
      } else {
        setIsConnected(false);
        setConnectedEmail(null);
      }
    } catch (err: unknown) {
      console.error("Error checking calendar connection:", err);
      setIsConnected(false);
      setConnectedEmail(null);
    } finally {
      setConnectionCheckLoading(false);
    }
  }, []);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const errorParam = params.get("error");
    const email = params.get("email");
    if (success === "calendar_connected" && email) {
      setIsConnected(true);
      setConnectedEmail(decodeURIComponent(email));
      toast.success(`Connected Google Calendar as ${decodeURIComponent(email)}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      checkConnection();
    } else if (errorParam) {
      toast.error(`Calendar connection failed: ${errorParam}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [checkConnection]);

  const startOAuth = useCallback(async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      toast.error(userError?.message || "Please sign in first");
      return;
    }
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !session?.access_token || !session.user?.id) {
      toast.error(refreshError?.message || "Please sign in again and try connecting.");
      return;
    }
    const accessToken = session.access_token;
    // Validate token has a user id - use session.user.id directly instead of manual JWT parsing
    if (!session.user?.id) {
      toast.error("Session invalid (no user id). Sign out and sign in again.");
      return;
    }
    setIsLoading(true);
    try {
      const redirectUri = `${window.location.origin}${window.location.pathname}`;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/google-calendar-oauth-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          redirect_uri: redirectUri,
          ...(brandId ? { brand_id: brandId } : {}),
          access_token: accessToken,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; auth_url?: string; debug?: unknown };
      const err = data?.error;
      if (!res.ok || err) {
        if (res.status === 401 && data?.debug) {
          console.error("[Calendar OAuth init 401]", data.debug);
        }
        if (err) {
          if (err.toLowerCase().includes("unauthorized") && !err.includes("Auth failed:")) {
            toast.error("Session expired or invalid. Sign out and sign in again, then try connecting Google Calendar.");
          } else if (err === "Calendar OAuth not configured") {
            toast.error(
              "Calendar OAuth is not configured. Set GOOGLE_CALENDAR_CLIENT_ID and GOOGLE_CALENDAR_CLIENT_SECRET in edge function secrets, or add google_calendar_client_id and google_calendar_client_secret to api_tokens for a brand you have access to. See docs/CALENDAR_OAUTH_SETUP.md.",
              { duration: 8000 }
            );
          } else {
            toast.error(err);
          }
        } else {
          toast.error("Failed to start connection");
        }
        return;
      }
      const authUrl = data?.auth_url;
      if (authUrl) {
        window.location.href = authUrl;
        return;
      }
      toast.error("No auth URL returned");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to connect Google Calendar");
    } finally {
      setIsLoading(false);
    }
  }, [brandId]);

  const disconnect = useCallback(async () => {
    try {
      const { data: row } = await supabase
        .from("os_user_google_calendar_tokens")
        .select("id")
        .maybeSingle();
      if (row) {
        const { error } = await supabase
          .from("os_user_google_calendar_tokens")
          .delete()
          .eq("id", row.id);
        if (error) throw error;
      }
      setIsConnected(false);
      setConnectedEmail(null);
      setEvents([]);
      toast.success("Google Calendar disconnected");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to disconnect");
    }
  }, []);

  const fetchEvents = useCallback(async (timeMin: string, timeMax: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    setEventsLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/user-calendar-api`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ operation: "list_events", timeMin, timeMax, ...(brandId ? { brand_id: brandId } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load events");
      setEvents(data.events ?? []);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to load events");
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [brandId]);

  const createEvent = useCallback(
    async (summary: string, start: string, end: string, description?: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please sign in first");
        return null;
      }
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/user-calendar-api`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            operation: "create_event",
            summary,
            start,
            end,
            description: description ?? "",
            ...(brandId ? { brand_id: brandId } : {}),
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to create event");
        toast.success("Event added to your calendar");
        return data;
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : "Failed to create event");
        return null;
      }
    },
    [brandId]
  );

  const deleteEvent = useCallback(
    async (eventId: string): Promise<boolean> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please sign in first");
        return false;
      }
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/user-calendar-api`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ operation: "delete_event", eventId, ...(brandId ? { brand_id: brandId } : {}) }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error((data as { error?: string })?.error || "Failed to delete event");
        toast.success("Event deleted");
        return true;
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : "Failed to delete event");
        return false;
      }
    },
    [brandId]
  );

  const updateEvent = useCallback(
    async (
      eventId: string,
      params: { summary?: string; start?: string; end?: string; description?: string }
    ): Promise<boolean> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please sign in first");
        return false;
      }
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/user-calendar-api`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ operation: "update_event", eventId, ...params, ...(brandId ? { brand_id: brandId } : {}) }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error((data as { error?: string })?.error || "Failed to update event");
        toast.success("Event updated");
        return true;
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : "Failed to update event");
        return false;
      }
    },
    [brandId]
  );

  return {
    isLoading,
    connectionCheckLoading,
    isConnected,
    connectedEmail,
    startOAuth,
    disconnect,
    checkConnection,
    events,
    eventsLoading,
    fetchEvents,
    createEvent,
    deleteEvent,
    updateEvent,
  };
}
